/**
 * Helper function to convert legacy timezone abbreviations to IANA format
 * @param tz - Timezone string (can be legacy abbreviation or IANA format)
 * @returns IANA timezone identifier
 */
export const normalizeTimezone = (tz: string | undefined): string => {
  if (!tz) return 'America/New_York';
  const legacyToIana: Record<string, string> = {
    IST: 'Asia/Kolkata',
    PST: 'America/Los_Angeles',
    EST: 'America/New_York',
    GMT: 'Europe/London',
    JST: 'Asia/Tokyo',
  };
  // If it's already an IANA timezone (contains '/'), return as is
  if (tz.includes('/')) return tz;
  // Otherwise, try to convert from legacy format
  return legacyToIana[tz.toUpperCase()] || tz;
};

/**
 * Formats a date/time string to a readable format in the user's timezone
 * @param dateTime - Date string in ISO format (e.g., "2025-11-04T03:30:07")
 * @param timezone - Optional IANA timezone identifier (defaults to browser's timezone)
 * @returns Formatted date string (e.g., "Nov 4, 2025, 3:30 AM") or original value if invalid
 */
export const formatDateTime = (dateTime: string | null | undefined, timezone?: string): string => {
  if (!dateTime) return '-';
  try {
    // Handle date format like "2025-11-14 11:16:19" (without timezone)
    // If the date string doesn't have timezone info, assume it's UTC from the server
    let dateString = dateTime.trim();
    let parsedDate: Date;

    // Check if it's in format "YYYY-MM-DD HH:mm:ss" (has space, no T, no Z, no timezone offset)
    const isDateWithoutTimezone =
      dateString &&
      dateString.includes(' ') &&
      !dateString.includes('T') &&
      !dateString.includes('Z') &&
      !dateString.match(/[+-]\d{2}:?\d{2}$/);

    if (isDateWithoutTimezone) {
      // Format: "YYYY-MM-DD HH:mm:ss" - treat as UTC
      // Replace space with T and add Z to make it explicit UTC
      dateString = dateString.replace(' ', 'T') + 'Z';
      parsedDate = new Date(dateString);
    } else if (
      dateString.includes('T') &&
      !dateString.includes('Z') &&
      !dateString.match(/[+-]\d{2}:?\d{2}$/)
    ) {
      // ISO format without timezone (e.g., "2025-11-14T11:16:19") - treat as UTC
      dateString = dateString + 'Z';
      parsedDate = new Date(dateString);
    } else {
      // Already has timezone info or is in ISO format with timezone
      parsedDate = new Date(dateTime);
    }

    if (isNaN(parsedDate.getTime())) {
      return dateTime;
    }

    // Use provided timezone or browser's timezone
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const userTimezone = timezone || browserTimezone;

    const formatted = parsedDate.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: userTimezone,
    });

    return formatted;
  } catch {
    return dateTime;
  }
};

/**
 * Strips HTML tags and converts HTML to plain text while preserving line breaks
 * Converts <p>, <div>, <br> tags to newlines
 * @param html - HTML string from Quill editor
 * @returns Plain text string with preserved line breaks
 */
export const stripHtmlToPlainText = (html: string | null | undefined): string => {
  if (!html) return '';

  // Create a temporary DOM element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Replace block elements with newlines
  const blockElements = tempDiv.querySelectorAll('p, div, br, li');
  blockElements.forEach(el => {
    if (el.tagName === 'BR') {
      el.replaceWith(document.createTextNode('\n'));
    } else {
      // Add newline before block elements
      const prev = el.previousSibling;
      if (prev && prev.textContent && !prev.textContent.endsWith('\n')) {
        el.parentNode?.insertBefore(document.createTextNode('\n'), el);
      }
      // Add newline after block elements
      const next = el.nextSibling;
      if (next && next.textContent && !next.textContent.startsWith('\n')) {
        el.parentNode?.insertBefore(document.createTextNode('\n'), next);
      }
    }
  });

  // Get text content and clean up multiple consecutive newlines
  let text = tempDiv.textContent || tempDiv.innerText || '';

  // Replace multiple consecutive newlines (3+) with double newline
  text = text.replace(/\n{3,}/g, '\n\n');

  // Trim leading/trailing whitespace but preserve intentional line breaks
  return text.trim();
};

/**
 * Converts HTML from Quill editor to Slack's markdown format
 * Preserves bold, italic, strikethrough, and line breaks
 * @param html - HTML string from Quill editor
 * @returns Slack markdown formatted string
 */
export const convertHtmlToSlackMarkdown = (html: string | null | undefined): string => {
  if (!html) return '';

  // Parse HTML into DOM
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Process the DOM tree recursively to convert formatting
  const processNode = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || '';
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      const tagName = element.tagName.toLowerCase();

      // Get the text content of children
      let childText = '';
      node.childNodes.forEach(child => {
        childText += processNode(child);
      });

      // Apply Slack markdown based on tag
      switch (tagName) {
        case 'strong':
        case 'b':
          return `*${childText}*`;
        case 'em':
        case 'i':
          return `_${childText}_`;
        case 's':
        case 'strike':
          return `~${childText}~`;
        case 'u':
          return childText; // Slack doesn't support underline
        case 'br':
          return '\n';
        case 'p':
          return childText + '\n\n';
        case 'div':
          return childText + '\n';
        case 'li':
          return '• ' + childText + '\n';
        case 'ul':
        case 'ol':
          return '\n' + childText;
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
          return `*${childText}*\n\n`; // Bold headers in Slack
        default:
          return childText;
      }
    }

    return '';
  };

  let result = '';
  tempDiv.childNodes.forEach(node => {
    result += processNode(node);
  });

  // Clean up excessive newlines (more than 2 consecutive)
  result = result.replace(/\n{3,}/g, '\n\n');

  // Trim leading/trailing whitespace
  return result.trim();
};

/**
 * Converts HTML from Quill editor to clean HTML suitable for email
 * Preserves formatting and converts to simple HTML
 * @param html - HTML string from Quill editor
 * @returns Clean HTML string
 */
export const convertHtmlToEmailHtml = (html: string | null | undefined): string => {
  if (!html) return '';

  // Parse HTML into DOM
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Process the DOM tree recursively to convert to clean HTML
  const processNode = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      // Escape HTML entities in text
      const text = node.textContent || '';
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      const tagName = element.tagName.toLowerCase();

      // Get the HTML content of children
      let childHtml = '';
      node.childNodes.forEach(child => {
        childHtml += processNode(child);
      });

      // Convert to clean HTML
      switch (tagName) {
        case 'strong':
        case 'b':
          return `<strong>${childHtml}</strong>`;
        case 'em':
        case 'i':
          return `<em>${childHtml}</em>`;
        case 's':
        case 'strike':
          return `<s>${childHtml}</s>`;
        case 'u':
          return `<u>${childHtml}</u>`;
        case 'br':
          return '<br>';
        case 'p':
          return `<p>${childHtml}</p>`;
        case 'div':
          return `<div>${childHtml}</div>`;
        case 'li':
          return `<li>${childHtml}</li>`;
        case 'ul':
          return `<ul>${childHtml}</ul>`;
        case 'ol':
          return `<ol>${childHtml}</ol>`;
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
          return `<${tagName}>${childHtml}</${tagName}>`;
        default:
          return childHtml;
      }
    }

    return '';
  };

  let result = '';
  tempDiv.childNodes.forEach(node => {
    result += processNode(node);
  });

  // Keep intentional empty lines from the editor.
  // Quill uses empty paragraphs for manual line breaks, so avoid collapsing them.
  result = result
    .replace(/<p>\s*<\/p>/gi, '<p><br></p>')
    .trim();

  return result;
};

/**
 * Converts HTML from Quill editor to plain text with better newline preservation
 * @param html - HTML string from Quill editor
 * @returns Plain text with preserved formatting structure
 */
export const convertHtmlToPlainText = (html: string | null | undefined): string => {
  if (!html) return '';

  // Parse HTML into DOM
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Process the DOM tree recursively to extract text with newlines
  const processNode = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || '';
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      const tagName = element.tagName.toLowerCase();

      // Get the text content of children
      let childText = '';
      node.childNodes.forEach(child => {
        childText += processNode(child);
      });

      // Apply formatting based on tag
      switch (tagName) {
        case 'br':
          return '\n';
        case 'p':
          return childText + '\n\n';
        case 'div':
          return childText + '\n';
        case 'li':
          return '• ' + childText + '\n';
        case 'ul':
        case 'ol':
          return '\n' + childText;
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
          return childText + '\n\n';
        case 'strong':
        case 'b':
        case 'em':
        case 'i':
        case 's':
        case 'strike':
        case 'u':
          // Strip formatting tags but keep text
          return childText;
        default:
          return childText;
      }
    }

    return '';
  };

  let result = '';
  tempDiv.childNodes.forEach(node => {
    result += processNode(node);
  });

  // Clean up excessive newlines (more than 2 consecutive)
  result = result.replace(/\n{3,}/g, '\n\n');

  // Trim leading/trailing whitespace
  return result.trim();
};
